/**
 * 페이지뷰 추적
 *
 * page_view 이벤트 수집 (SPA 히스토리 변경 포함)
 * - History API pushState/replaceState 패치
 * - popstate 이벤트 감지
 * - 초기 페이지 로드 시 1회 발행
 */

import type { LambAnalytics } from '../core/sdk';

/** 페이지뷰 추적기 */
export class PageViewTracker {
  private sdk: LambAnalytics;
  private active = false;
  private lastUrl = '';
  private popstateHandler: (() => void) | null = null;
  private originalPushState: typeof history.pushState | null = null;
  private originalReplaceState: typeof history.replaceState | null = null;

  constructor(sdk: LambAnalytics) {
    this.sdk = sdk;
  }

  start(): void {
    if (this.active || typeof window === 'undefined') return;
    this.active = true;
    this.lastUrl = window.location.href;

    this.patchHistoryApi();
    this.popstateHandler = () => this.onRouteChange();
    window.addEventListener('popstate', this.popstateHandler);

    this.trackPageView();
  }

  stop(): void {
    if (!this.active || typeof window === 'undefined') return;
    this.active = false;

    this.restoreHistoryApi();
    if (this.popstateHandler) {
      window.removeEventListener('popstate', this.popstateHandler);
      this.popstateHandler = null;
    }
  }

  private patchHistoryApi(): void {
    if (typeof history === 'undefined') return;

    this.originalPushState = history.pushState.bind(history);
    this.originalReplaceState = history.replaceState.bind(history);

    const self = this;

    history.pushState = function (
      data: unknown,
      unused: string,
      url?: string | URL | null,
    ): void {
      self.originalPushState!(data, unused, url);
      self.onRouteChange();
    };

    history.replaceState = function (
      data: unknown,
      unused: string,
      url?: string | URL | null,
    ): void {
      self.originalReplaceState!(data, unused, url);
      self.onRouteChange();
    };
  }

  private restoreHistoryApi(): void {
    if (typeof history === 'undefined') return;

    if (this.originalPushState) {
      history.pushState = this.originalPushState;
      this.originalPushState = null;
    }
    if (this.originalReplaceState) {
      history.replaceState = this.originalReplaceState;
      this.originalReplaceState = null;
    }
  }

  private onRouteChange(): void {
    const currentUrl = window.location.href;
    if (currentUrl === this.lastUrl) return;
    this.lastUrl = currentUrl;

    // 약간의 딜레이를 두어 document.title이 업데이트되도록 함
    setTimeout(() => this.trackPageView(), 0);
  }

  private trackPageView(): void {
    if (!this.active) return;

    this.sdk.track('page_view', {
      page_location: window.location.href,
      page_title: document.title,
      page_referrer: document.referrer,
    });
  }
}
